import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";
import { v4 as uuidv4 } from 'uuid';
import config from '@/config/env';
import { notificationService } from "@/api/notificationService";

// Initialize Firebase
const firebaseApp = initializeApp(config.firebaseConfig);

// Kiểm tra xem trình duyệt đã hỗ trợ Firebase Messaging chưa
let messaging: any = null;

// Khởi tạo messaging nếu trình duyệt hỗ trợ
const initMessaging = async () => {
  try {
    if (await isSupported()) {
      messaging = getMessaging(firebaseApp);
      return true;
    }
  } catch (err) {
    console.error("Firebase messaging not supported", err);
  }
  return false;
};

// Kiểm tra trình duyệt có hỗ trợ notifications không
export const isNotificationsSupported = () => {
  return (
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
};

// Yêu cầu quyền thông báo và lấy token FCM
export const requestNotificationPermission = async (): Promise<string | null> => {
  if (!isNotificationsSupported()) {
    console.log("Notifications are not supported in this browser");
    return null;
  }
  
  try {
    // Khởi tạo Firebase Messaging
    const isMessagingSupported = await initMessaging();
    if (!isMessagingSupported || !messaging) {
      console.log("Firebase messaging is not supported");
      return null;
    }

    // Yêu cầu quyền thông báo
    const permission = await Notification.requestPermission();
    
    // Nếu người dùng cho phép, lấy token FCM
    if (permission === "granted") {
      const currentToken = await getToken(messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY || "BHL0ObaWyG-98IpIv7IMoJCv8SlyhCv_0auuz-XfLac6kHV4mjTuaHXriFJtANnpLUjhaO-W4F-StSCjK5mP0Rw"
      });
      
      if (currentToken) {
        console.log("FCM token:", currentToken);
        return currentToken;
      } else {
        console.log("No FCM token available");
      }
    } else {
      console.log("Notification permission denied");
    }
  } catch (error) {
    console.error("Error requesting notification permission:", error);
  }
  
  return null;
};

// Lấy token FCM đã lưu
export const getFCMToken = (): string | null => {
  return localStorage.getItem("fcmToken");
};

// Bộ lắng nghe tin nhắn Firebase
export const onMessageListener = (): Promise<any> => {
  return new Promise((resolve) => {
    if (!messaging) {
      resolve(null);
      return;
    }
    
    onMessage(messaging, (payload) => {
      console.log("Message received:", payload);
      resolve(payload);
    });
  });
};

// Gửi token lên server
export const sendTokenToServer = async (token: string) => {
  // Nếu token đã được gửi lên server, không gửi nữa
  const tokenSent = localStorage.getItem("fcmTokenSent");
  const savedToken = localStorage.getItem("fcmTokenSaved");
  
  // Nếu token đã được gửi và chưa thay đổi, không gửi nữa
  if (tokenSent === "true" && savedToken === token) {
    console.log("Token already sent to server");
    return;
  }
  
  try {
    // Lấy hoặc tạo device ID
    let deviceId = localStorage.getItem("deviceId");
    if (!deviceId) {
      deviceId = _generateDeviceId();
      localStorage.setItem("deviceId", deviceId);
    }
    
    // Thông tin về thiết bị
    const deviceInfo = {
      token,
      device_type: _getDeviceType(),
      device_id: deviceId,
      app_version: '1.0.0', // Phiên bản ứng dụng
      platform_version: _getPlatformVersion(),
    };
    
    // Gửi thông tin lên server
    await notificationService.registerToken(deviceInfo);
    
    // Lưu token vào localStorage
    localStorage.setItem("fcmToken", token);
    
    console.log("Token sent to server");
  } catch (error) {
    console.error("Error sending token to server:", error);
  }
};

// Xác định loại thiết bị
const _getDeviceType = (): string => {
  const ua = navigator.userAgent;
  
  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/Windows Phone/i.test(ua)) return 'windows_phone';
  
  // Kiểm tra thiết bị mobile khác
  if (/Mobi|Android/i.test(ua)) return 'mobile';
  
  return 'desktop';
};

// Tạo ID thiết bị
const _generateDeviceId = (): string => {
  // Sử dụng UUID hoặc tạo ID ngẫu nhiên
  let deviceId = uuidv4();
  
  // Lưu vào localStorage để sử dụng lại
  localStorage.setItem("deviceId", deviceId);
  
  return deviceId;
};

// Lấy phiên bản hệ điều hành
const _getPlatformVersion = (): string => {
  const ua = navigator.userAgent;
  let platformVersion = 'unknown';
  
  // Trích xuất thông tin phiên bản từ User Agent
  // Windows
  let match = ua.match(/(Windows NT\s(\d+\.\d+))/i);
  if (match) return `Windows ${match[2]}`;
  
  // macOS
  match = ua.match(/Mac OS X\s(\d+[._]\d+)/i);
  if (match) return `macOS ${match[1].replace('_', '.')}`;
  
  // iOS
  match = ua.match(/OS\s(\d+[._]\d+)/i);
  if (match) return `iOS ${match[1].replace('_', '.')}`;
  
  // Android
  match = ua.match(/Android\s(\d+\.\d+)/i);
  if (match) return `Android ${match[1]}`;
  
  return platformVersion;
}; 